/*
 * Global theme signal — mirrors the `data-theme` attribute on <html>.
 *
 * Concept B.3: theme is a runtime attribute switch, not a re-render. Components
 * that need to react (canvas charts, map tile layers, etc.) subscribe to
 * `themeSignal`; components that just consume CSS tokens don't need to know
 * anything — the cascade handles it.
 *
 * Read order at boot:
 *   1. explicit `data-theme` on <html>  (persisted, wins)
 *   2. localStorage entry               (user preference across reloads)
 *   3. `prefers-color-scheme`           (OS default)
 *   4. fallback: BRAND.defaultTheme     (dark for HVACloud; a brand may prefer light)
 */

import { signal } from '@preact/signals';
import { BRAND } from '../brand/brand';

export type ThemeMode = 'dark' | 'light';

const STORAGE_KEY = 'hv-theme';

function resolveInitialTheme(): ThemeMode {
  if (typeof document === 'undefined') return 'dark';

  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'dark' || attr === 'light') return attr;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage may throw in privacy modes — fall through.
  }

  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return BRAND.defaultTheme;
}

const initial = resolveInitialTheme();

// Reflect resolved value onto the DOM immediately so first paint matches. If
// nothing was set (SSR would be an issue, but this is Vite/SPA) this is the
// first write.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('data-theme', initial);
}

export const themeSignal = signal<ThemeMode>(initial);

/** Switch to an explicit mode; writes DOM attribute + localStorage + signal. */
export function setTheme(mode: ThemeMode): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', mode);
  }
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore
  }
  themeSignal.value = mode;
}

/** Flip between dark and light. Convenience for a single toggle button. */
export function toggleTheme(): void {
  setTheme(themeSignal.value === 'dark' ? 'light' : 'dark');
}
